/**
 * Role-specific dashboards. Each is a focused widget collection for one
 * operational role, driven by the new /api/sidebar/counters endpoint plus
 * each module's existing list endpoints.
 *
 * The Dashboard page picks which one to render based on the logged-in user's
 * role. CEO / Super Admin / Admin / Management still use the rich legacy
 * Dashboard (Command Center, Risk Radar, finance pulse).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/app/ui-bits";
import { useAuth } from "@/contexts/AuthContext";
import { roleLabel } from "@/lib/roles";
import {
  Coins, Users as UsersIcon, Handshake, Buildings, Package,
  CheckSquare, Clock, Warning, ShieldCheck, ListChecks, ArrowRight,
  Receipt, Bank, FileText, IdentificationBadge, Calendar,
  CalendarBlank, Wrench, Truck, Briefcase, AirplaneTilt,
} from "@phosphor-icons/react";

const TONE_BORDER = {
  blue: "border-l-[#1E5CBA]", emerald: "border-l-emerald-500",
  amber: "border-l-amber-500", red: "border-l-rose-500",
  indigo: "border-l-indigo-500", violet: "border-l-violet-500",
  slate: "border-l-slate-300",
};
const TONE_TEXT = {
  blue: "text-[#1E5CBA]", emerald: "text-emerald-700",
  amber: "text-amber-700", red: "text-rose-700",
  indigo: "text-indigo-700", violet: "text-violet-700",
  slate: "text-slate-700",
};

export function Tile({ testid, icon: Icon, label, value, tone = "slate", hint, link, sub }) {
  const content = (
    <>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 truncate">{label}</div>
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
      {sub && <div className="text-[11px] text-slate-600 mt-1">{sub}</div>}
    </>
  );
  const cls = `dc-card p-3 border-l-4 ${TONE_BORDER[tone]} transition`;
  if (link) {
    return (
      <Link to={link} data-testid={testid} className={`${cls} hover:shadow-md block`}>{content}</Link>
    );
  }
  return <div data-testid={testid} className={cls}>{content}</div>;
}

export function Section({ title, subtitle, icon: Icon, action, children }) {
  return (
    <section className="space-y-2">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon size={15} className="text-[#1E5CBA]" />}
            <h3 className="font-display text-[15px] font-semibold text-slate-900">{title}</h3>
          </div>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// ── Generic per-role dashboard wrapper ─────────────────────────────
function DashHeader({ eyebrow, title, description, children }) {
  return (
    <PageHeader
      eyebrow={eyebrow}
      title={title}
      description={description}
      actions={children}
    />
  );
}

// ── Finance dashboard ──────────────────────────────────────────────
export function FinanceDashboard({ counters }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [pulseR, invR] = await Promise.all([
          api.get("/dashboard/finance-pulse").catch(() => ({ data: null })),
          api.get("/finance/invoices").catch(() => ({ data: { items: [] } })),
        ]);
        setData({ pulse: pulseR.data, invoices: invR.data.items || invR.data || [] });
      } catch (e) { /* silent */ }
    })();
  }, []);

  const pulse = data?.pulse || {};
  const invoices = data?.invoices || [];
  const open_invoices = invoices.filter((i) => !["paid", "cancelled"].includes(i.status)).length;

  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="finance-dashboard">
      <DashHeader eyebrow="Finance · live operations"
        title="Finance Dashboard"
        description="Pending reviews, cashflow, invoices, reconciliation, and donor deadlines at a glance." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile testid="fd-approvals"  icon={CheckSquare} label="Approvals pending" value={counters.approvals_pending ?? 0}
          tone={counters.approvals_pending ? "amber" : "slate"} link="/admin/approvals" />
        <Tile testid="fd-finance"    icon={Coins}       label="Finance queue"     value={counters.finance_pending ?? 0}
          tone={counters.finance_pending ? "amber" : "slate"} link="/admin/finance" />
        <Tile testid="fd-cashflow"   icon={Bank}        label="Net cashflow MTD"  value={formatCurrency(pulse.cashflow_mtd || 0)} tone="blue" />
        <Tile testid="fd-invoices"   icon={Receipt}     label="Open invoices"     value={open_invoices} tone={open_invoices ? "amber" : "slate"} link="/admin/finance" />
        <Tile testid="fd-revenue"    icon={Coins}       label="Revenue YTD"       value={formatCurrency(pulse.revenue_ytd || 0)} tone="emerald" />
        <Tile testid="fd-costs"      icon={Coins}       label="Costs YTD"         value={formatCurrency(pulse.costs_ytd || 0)} tone="red" />
      </div>

      <Section title="Finance workspace" icon={Coins}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <QuickLink to="/admin/finance" icon={Coins}    label="Finance Ledger"     desc="Entries, project costs, ledger views" />
          <QuickLink to="/admin/finance" icon={Receipt}  label="Invoice Register"   desc="Open, sent, paid invoices" />
          <QuickLink to="/admin/finance" icon={Bank}     label="Bank Reconciliation"desc="Match bank vs ledger" />
          <QuickLink to="/admin/finance" icon={FileText} label="Donor Reporting"    desc="Donor packs and deadlines" />
        </div>
      </Section>
    </div>
  );
}

// ── HR dashboard ──────────────────────────────────────────────────
export function HRDashboard({ counters }) {
  const [data, setData] = useState({ staff: 0, contracts_expiring: 0, leave_pending: 0, warnings_pending: 0 });
  useEffect(() => {
    (async () => {
      try {
        const [staffR, contractsR] = await Promise.all([
          api.get("/hr/staff").catch(() => ({ data: { items: [] } })),
          api.get("/hr/dashboard-summary").catch(() => ({ data: {} })),
        ]);
        setData({
          staff: (staffR.data.items || staffR.data || []).length,
          contracts_expiring: contractsR.data.contracts_expiring || 0,
          leave_pending: contractsR.data.leave_pending || 0,
          warnings_pending: contractsR.data.warnings_pending || 0,
        });
      } catch (e) {/* silent */}
    })();
  }, []);
  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="hr-dashboard">
      <DashHeader eyebrow="Human Resources · live operations"
        title="HR Dashboard"
        description="Staff, contracts, payroll requests, warnings, defenses, and leave at a glance." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile testid="hd-approvals" icon={CheckSquare} label="Approvals pending" value={counters.approvals_pending ?? 0}
          tone={counters.approvals_pending ? "amber" : "slate"} link="/admin/approvals" />
        <Tile testid="hd-hr-queue"  icon={ListChecks}  label="HR queue"          value={counters.hr_pending ?? 0}
          tone={counters.hr_pending ? "amber" : "slate"} link="/admin/hr" />
        <Tile testid="hd-staff"     icon={UsersIcon}   label="Active staff"      value={data.staff} tone="blue" link="/admin/hr" />
        <Tile testid="hd-contracts" icon={IdentificationBadge} label="Contracts ≤30d" value={data.contracts_expiring}
          tone={data.contracts_expiring ? "amber" : "slate"} />
        <Tile testid="hd-leave"     icon={Calendar}    label="Leave pending"     value={data.leave_pending}
          tone={data.leave_pending ? "amber" : "slate"} />
        <Tile testid="hd-ethics"    icon={ShieldCheck} label="Ethics queue"      value={counters.ethics_pending ?? 0}
          tone={counters.ethics_pending ? "amber" : "slate"} link="/admin/ethics" />
      </div>

      <Section title="HR workspace" icon={UsersIcon}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <QuickLink to="/admin/hr" icon={UsersIcon}            label="Staff Directory" desc="All employees by status" />
          <QuickLink to="/admin/hr" icon={IdentificationBadge}  label="Contracts"        desc="Renewals, expiries" />
          <QuickLink to="/admin/hr" icon={Coins}                label="Salary Requests"  desc="Payroll workflow" />
          <QuickLink to="/admin/hr" icon={Warning}              label="Warnings & Defense" desc="Discipline workflow" />
        </div>
      </Section>
    </div>
  );
}

// ── Procurement dashboard ─────────────────────────────────────────
export function ProcurementDashboard({ counters }) {
  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="proc-role-dashboard">
      <DashHeader eyebrow="Procurement · live operations"
        title="Procurement Dashboard"
        description="Pending requests, RFQs, evaluations, POs, GRNs, supplier docs, and approvals." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile testid="pd-approvals" icon={CheckSquare} label="Approvals pending" value={counters.approvals_pending ?? 0}
          tone={counters.approvals_pending ? "amber" : "slate"} link="/admin/approvals" />
        <Tile testid="pd-proc"      icon={Handshake}   label="Procurement queue" value={counters.procurement_pending ?? 0}
          tone={counters.procurement_pending ? "amber" : "slate"} link="/admin/procurement" />
        <Tile testid="pd-onboarding" icon={IdentificationBadge} label="Vendor onboarding" value={counters.vendor_onboarding_pending ?? 0}
          tone={counters.vendor_onboarding_pending ? "amber" : "slate"} link="/admin/procurement?tab=suppliers" />
        <Tile testid="pd-mytasks"   icon={ListChecks}  label="My open tasks"     value={counters.tasks_mine_pending ?? 0} tone="slate" link="/admin/tasks" />
        <Tile testid="pd-notifs"    icon={Briefcase}   label="My notifications"  value={counters.notifications_unread ?? 0} tone="slate" />
        <Tile testid="pd-ethics"    icon={ShieldCheck} label="Ethics queue"      value={counters.ethics_pending ?? 0} tone="slate" link="/admin/ethics" />
      </div>

      <Section title="Procurement workspace" icon={Handshake}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <QuickLink to="/admin/procurement"                icon={Handshake}            label="Procurement Pipeline" desc="Active records by phase" />
          <QuickLink to="/admin/procurement?tab=suppliers"  icon={IdentificationBadge}  label="Vendors & Suppliers"  desc="Registry + onboarding" />
          <QuickLink to="/admin/procurement?tab=pr_po"      icon={Truck}                label="PR / PO"              desc="Open POs and GRNs" />
          <QuickLink to="/admin/procurement?tab=registers"  icon={FileText}             label="Contract Register"    desc="Active contracts" />
        </div>
      </Section>
    </div>
  );
}

// ── Project Manager dashboard ─────────────────────────────────────
export function PMDashboard({ counters }) {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/projects").catch(() => ({ data: { items: [] } }));
        setProjects((r.data.items || r.data || []).slice(0, 8));
      } catch (e) {/* silent */}
    })();
  }, []);
  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="pm-dashboard">
      <DashHeader eyebrow="Project Management · live operations"
        title="Project Manager Dashboard"
        description="Assigned projects, deliverables, overdue tasks, project risks, and budget usage." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile testid="md-approvals" icon={CheckSquare} label="Approvals pending" value={counters.approvals_pending ?? 0}
          tone={counters.approvals_pending ? "amber" : "slate"} link="/admin/approvals" />
        <Tile testid="md-overdue"   icon={Warning}     label="Overdue tasks"     value={counters.projects_pending ?? 0}
          tone={counters.projects_pending ? "red" : "slate"} link="/admin/tasks" />
        <Tile testid="md-projects"  icon={Buildings}   label="Projects in view"  value={projects.length} tone="blue" link="/admin/projects" />
        <Tile testid="md-procurement" icon={Handshake} label="Linked procurement" value={counters.procurement_pending ?? 0} tone="slate" />
        <Tile testid="md-mytasks"   icon={ListChecks}  label="My tasks"          value={counters.tasks_mine_pending ?? 0} tone="slate" link="/admin/tasks" />
        <Tile testid="md-assets"    icon={Package}     label="Asset alerts"      value={counters.assets_pending ?? 0}
          tone={counters.assets_pending ? "amber" : "slate"} link="/admin/assets" />
      </div>

      <Section title="My projects" icon={Buildings}
        action={<Link to="/admin/projects" className="text-xs text-[#1E5CBA] hover:underline">Open all <ArrowRight size={12} className="inline ml-0.5" /></Link>}>
        {projects.length === 0 ? (
          <div className="dc-card p-6 text-center text-xs text-slate-400">No projects yet.</div>
        ) : (
          <ul className="dc-card divide-y divide-slate-100">
            {projects.map((p) => (
              <li key={p.id || p.project_code}>
                <Link to={`/admin/projects?new=${p.id || ""}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                  <Buildings size={16} className="text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{p.name || p.project_code}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {p.client || "—"} · {p.status || "active"}{p.end_date ? ` · ends ${formatDate(p.end_date)}` : ""}
                    </div>
                  </div>
                  <span className="font-mono-alt text-xs tabular-nums text-slate-600">
                    {p.budget_usd ? formatCurrency(p.budget_usd) : "—"}
                  </span>
                  <ArrowRight size={14} className="text-slate-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

// ── Asset Manager dashboard ───────────────────────────────────────
export function AssetMgrDashboard({ counters }) {
  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="asset-dashboard">
      <DashHeader eyebrow="Asset Management · live operations"
        title="Asset Manager Dashboard"
        description="Calibration, maintenance, mobilisations, rentals, and asset incidents." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile testid="ad-approvals" icon={CheckSquare} label="Approvals pending" value={counters.approvals_pending ?? 0}
          tone={counters.approvals_pending ? "amber" : "slate"} link="/admin/approvals" />
        <Tile testid="ad-asset"     icon={Package}     label="Asset alerts"      value={counters.assets_pending ?? 0}
          tone={counters.assets_pending ? "amber" : "slate"} link="/admin/assets" />
        <Tile testid="ad-cal"       icon={Wrench}      label="Calibration ≤30d"  value="" hint="opens asset register" tone="slate" link="/admin/assets" />
        <Tile testid="ad-maint"     icon={Wrench}      label="Maintenance ≤30d"  value="" hint="opens asset register" tone="slate" link="/admin/assets" />
        <Tile testid="ad-mob"       icon={AirplaneTilt} label="Mobilisations"    value="" hint="opens movements" tone="slate" link="/admin/assets" />
        <Tile testid="ad-mytasks"   icon={ListChecks}  label="My tasks"          value={counters.tasks_mine_pending ?? 0} tone="slate" link="/admin/tasks" />
      </div>

      <Section title="Asset workspace" icon={Package}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <QuickLink to="/admin/assets" icon={Package}      label="Asset Register"     desc="All assets, status, allocation" />
          <QuickLink to="/admin/assets" icon={Wrench}       label="Calibration"        desc="Due, completed, overdue" />
          <QuickLink to="/admin/assets" icon={Wrench}       label="Maintenance"        desc="Service schedule" />
          <QuickLink to="/admin/assets" icon={AirplaneTilt} label="Mobilisations"      desc="Deployments to sites" />
        </div>
      </Section>
    </div>
  );
}

// ── Compliance Officer dashboard ──────────────────────────────────
export function ComplianceDashboard({ counters }) {
  return (
    <div className="p-5 md:p-8 space-y-6" data-testid="compliance-dashboard">
      <DashHeader eyebrow="Compliance · live operations"
        title="Compliance Officer Dashboard"
        description="Licenses, permits, regulatory documents, renewals, and tax clearance." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Tile testid="cd-approvals"  icon={CheckSquare} label="Approvals pending" value={counters.approvals_pending ?? 0}
          tone={counters.approvals_pending ? "amber" : "slate"} link="/admin/approvals" />
        <Tile testid="cd-compliance" icon={ShieldCheck} label="Compliance items"  value={counters.compliance_pending ?? 0}
          tone={counters.compliance_pending ? "amber" : "slate"} link="/admin/compliance" />
        <Tile testid="cd-mytasks"    icon={ListChecks}  label="My tasks"          value={counters.tasks_mine_pending ?? 0} tone="slate" link="/admin/tasks" />
        <Tile testid="cd-notifs"     icon={Briefcase}   label="My notifications"  value={counters.notifications_unread ?? 0} tone="slate" />
      </div>

      <Section title="Compliance workspace" icon={ShieldCheck}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <QuickLink to="/admin/compliance" icon={ShieldCheck} label="Compliance Tracker" desc="Licenses, permits, expiries" />
          <QuickLink to="/admin/compliance" icon={FileText}    label="Documents"          desc="Regulatory documents" />
          <QuickLink to="/admin/compliance" icon={Clock}       label="Renewals"           desc="Items due in 60 days" />
          <QuickLink to="/admin/calendar"   icon={CalendarBlank} label="Calendar"          desc="Compliance deadlines" />
        </div>
      </Section>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, desc }) {
  return (
    <Link to={to} className="dc-card p-3 hover:shadow-md transition flex items-start gap-3">
      <Icon size={20} className="text-[#1E5CBA] shrink-0" />
      <div className="min-w-0">
        <div className="font-display text-sm font-semibold text-slate-900 truncate">{label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5 truncate">{desc}</div>
      </div>
    </Link>
  );
}

// ── Roleless wrapper: load counters once and pick a dashboard ─────
export function RoleDashboardSwitcher({ children }) {
  const { user } = useAuth();
  const [counters, setCounters] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sidebar/counters");
        setCounters(data || {});
      } catch (e) {/* silent */}
    })();
  }, []);
  return typeof children === "function" ? children({ user, counters }) : children;
}
